import React, { useState, useEffect } from 'react';
import { ReferralRequest, CandidateRating as CandidateRatingType, referralsService } from '../../services/referrals.service';
import CandidateRating from './CandidateRating';
import './CandidateProfileModal.css';

interface CandidateProfileModalProps {
  request: ReferralRequest;
  onClose: () => void;
  darkMode?: boolean;
}

interface CandidateHistory {
  applications: Array<{
    companyName: string;
    position: string;
    date: Date;
    status: 'pending' | 'accepted' | 'declined';
    declineReason?: string;
  }>;
  skills: Array<{
    name: string;
    endorsed: number;
    level: 'beginner' | 'intermediate' | 'expert';
  }>;
  education?: Array<{
    degree: string;
    field: string;
    school: string;
    year: number;
  }>;
  experience?: Array<{
    title: string;
    company: string;
    duration: string;
    current: boolean;
  }>;
}

const CandidateProfileModal: React.FC<CandidateProfileModalProps> = ({ 
  request, 
  onClose,
  darkMode = false 
}) => {
  const [candidateRating, setCandidateRating] = useState<CandidateRatingType | null>(null);
  const [candidateHistory, setCandidateHistory] = useState<CandidateHistory | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'history' | 'evaluation'>('overview');

  useEffect(() => {
    loadCandidateData();
  }, [request.candidateId]);

  const loadCandidateData = async () => {
    try {
      const rating = await referralsService.getCandidateRating(request.candidateId);
      setCandidateRating(rating);
      
      // Mock candidate history for demonstration
      setCandidateHistory({
        applications: [
          {
            companyName: 'TechCorp',
            position: 'Senior Developer',
            date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
            status: 'declined',
            declineReason: 'Looking for more experience in cloud technologies'
          },
          {
            companyName: 'StartupXYZ',
            position: 'Full Stack Engineer',
            date: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000),
            status: 'accepted'
          }
        ],
        skills: request.candidateSkills.map((skill, idx) => ({
          name: skill,
          endorsed: Math.floor(Math.random() * 50) + 10,
          level: idx === 0 ? 'expert' : idx < 3 ? 'intermediate' : 'beginner' as any
        })),
        education: [
          {
            degree: 'B.S.',
            field: 'Computer Science',
            school: 'State University',
            year: 2018
          }
        ],
        experience: [
          {
            title: request.candidateTitle,
            company: 'Current Company',
            duration: request.candidateExperience,
            current: true
          }
        ]
      });
    } catch (error) {
      console.error('Error loading candidate data:', error);
    }
  };

  const getSkillLevelColor = (level: string) => {
    switch (level) {
      case 'expert': return '#10b981';
      case 'intermediate': return '#3b82f6';
      case 'beginner': return '#f59e0b';
      default: return '#6b7280';
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className={`candidate-profile-modal ${darkMode ? 'dark' : 'light'}`} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Candidate Profile</h2>
          <button className="close-btn" onClick={onClose}>
            <span className="material-icons">close</span>
          </button>
        </div>

        <div className="candidate-header">
          <img 
            src={request.candidateAvatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${request.candidateEmail}`}
            alt={request.candidateName}
            className="candidate-large-avatar"
          />
          <div className="candidate-header-info">
            <h3>{request.candidateName}</h3>
            <p className="candidate-title">{request.candidateTitle}</p>
            <p className="candidate-email">{request.candidateEmail}</p>
            <p className="candidate-experience">{request.candidateExperience} of experience</p>
          </div>
          <div className="candidate-header-rating">
            <CandidateRating 
              candidateId={request.candidateId} 
              candidateName={request.candidateName}
              showDetails={false}
              darkMode={darkMode}
            />
          </div>
        </div>

        <div className="modal-tabs">
          <button 
            className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button 
            className={`tab ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            Application History
          </button>
          <button 
            className={`tab ${activeTab === 'evaluation' ? 'active' : ''}`}
            onClick={() => setActiveTab('evaluation')}
          >
            Evaluation
          </button>
        </div>

        <div className="modal-content">
          {activeTab === 'overview' && (
            <div className="overview-content">
              <div className="section">
                <h4>Message</h4>
                <p className="candidate-message">{request.message}</p>
              </div>

              <div className="section">
                <h4>Skills</h4>
                <div className="skills-grid">
                  {candidateHistory?.skills.map((skill) => (
                    <div key={skill.name} className="skill-item">
                      <div className="skill-header">
                        <span className="skill-name">{skill.name}</span>
                        <span 
                          className="skill-level" 
                          style={{ color: getSkillLevelColor(skill.level) }}
                        >
                          {skill.level}
                        </span>
                      </div>
                      <div className="skill-endorsements">
                        {skill.endorsed} endorsements
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {candidateHistory?.education && (
                <div className="section">
                  <h4>Education</h4>
                  {candidateHistory.education.map((edu, idx) => (
                    <div key={idx} className="education-item">
                      <p className="degree">{edu.degree} in {edu.field}</p>
                      <p className="school">{edu.school} • {edu.year}</p>
                    </div>
                  ))}
                </div>
              )}

              {candidateHistory?.experience && (
                <div className="section">
                  <h4>Experience</h4>
                  {candidateHistory.experience.map((exp, idx) => (
                    <div key={idx} className="experience-item">
                      <p className="job-title">{exp.title}</p>
                      <p className="company">{exp.company} • {exp.duration}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'history' && (
            <div className="history-content">
              <h4>Previous Applications</h4>
              {candidateHistory?.applications.map((app, idx) => (
                <div key={idx} className={`application-item ${app.status}`}>
                  <div className="app-info">
                    <h5>{app.position} at {app.companyName}</h5>
                    <p className="app-date">{new Date(app.date).toLocaleDateString()}</p>
                  </div>
                  <div className={`app-status ${app.status}`}>
                    {app.status}
                  </div>
                  {app.declineReason && (
                    <div className="decline-reason">
                      <strong>Feedback:</strong> {app.declineReason}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {activeTab === 'evaluation' && (
            <div className="evaluation-content">
              <div className="coming-soon-section">
                <div className="coming-soon-icon">
                  <span className="material-icons" style={{ fontSize: '64px' }}>assessment</span>
                </div>
                <h3>Candidate Evaluation Coming Soon</h3>
                <p>Our AI-powered candidate evaluation system will provide:</p>
                <ul className="feature-list">
                  <li>Quality scores based on application history</li>
                  <li>Success rate predictions</li>
                  <li>Risk assessment and recommendations</li>
                  <li>Performance tracking across multiple applications</li>
                </ul>
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="secondary-btn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default CandidateProfileModal;